//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

// Central API response types used by the domain services and the domain
// hooks/context providers.
//
import { type XgtTypeName, type XgtValue } from '@/services/xgt';

// -----------------------------------------------------------------------
// Schema types
// -----------------------------------------------------------------------

export interface SchemaProperty {
  name: string;
  type: XgtTypeName;
  leaf_type?: XgtTypeName;
  depth?: number | string;
}

/** Node frame as returned by the API. */
export interface RawNodeFrame {
  name: string;
  key: string;
  properties: SchemaProperty[];
}

/** Edge frame as returned by the API. */
export interface RawEdgeFrame {
  name: string;
  source: string;
  target: string;
  source_key: string;
  target_key: string;
  properties: SchemaProperty[];
}

/** Node frame after addMetadataToSchema enrichment. */
export interface NodeFrame extends RawNodeFrame {
  key_index: number;
}

/** Edge frame after addMetadataToSchema enrichment. */
export interface EdgeFrame extends RawEdgeFrame {
  source_index: number;
  target_index: number;
  source_key_index: number;
  target_key_index: number;
}

/** Raw schema from the API before metadata enrichment. */
export interface RawGraphSchema {
  graph: string;
  nodes: RawNodeFrame[];
  edges: RawEdgeFrame[];
  errors?: string[];
}

/** Enriched schema with computed index fields. */
export interface GraphSchema {
  graph: string;
  nodes: NodeFrame[];
  edges: EdgeFrame[];
  errors?: string[];
}

// -----------------------------------------------------------------------
// Dataset types
// -----------------------------------------------------------------------

// One frame (vertex, edge or table) in a dataset, as returned by
// datasets_info.
export interface DatasetFrameInfo {
  name: string;
  fully_qualified_name: string;
  num_rows: number;
  // Per-frame permissions the backend resolves for the current user.
  create_rows: boolean;
  delete_frame: boolean;
  // The frame's own columns, sent for every frame in every response.  Unlike
  // `DatasetInfo.graph_schema` this covers table frames too, so it is the only
  // type source that works for all three frame kinds.
  schema: SchemaProperty[];
  // Which column identifies a row.  A vertex frame's, so null on the other two
  // kinds.  Read from here rather than found in `graph_schema` by name: that
  // projection spells its frames with the namespace stripped, while a graph
  // whose members have no alias spells `name` above fully qualified, so the two
  // do not match and every key went missing.
  key?: string | null;
}

// An edge frame additionally names its endpoint frames, and the columns the join
// is made of.
export interface DatasetEdgeFrameInfo extends DatasetFrameInfo {
  // Display names, which cannot identify a frame: two namespaces may each hold a
  // `Person`, and an edge may end on a vertex frame in another namespace.
  source_frame: string;
  target_frame: string;
  // The frame's own two columns that carry the join.
  source_key: string;
  target_key: string;
  // The endpoints as names that do identify them, for looking up what they are.
  source_fully_qualified_name: string;
  target_fully_qualified_name: string;
}

// A table frame reports whether the server reaps it on its own, which is what
// distinguishes a query results table from one the user created deliberately.
export interface DatasetTableFrameInfo extends DatasetFrameInfo {
  auto_delete: boolean;
}

export interface DatasetInfo {
  name: string;
  // Which of the two a dataset is.  Not derivable from the arrays below: a
  // graph never holds tables, but an empty namespace and one holding only
  // vertex frames look exactly like a graph.
  readonly kind: 'graph' | 'namespace';
  delete_frame: boolean;
  // Optional: the backend returns these arrays for graph/namespace datasets,
  // but a consumer can't assume they're present for every response shape.
  vertices?: DatasetFrameInfo[];
  edges?: DatasetEdgeFrameInfo[];
  // Table frames are never graph members, so this is populated only for
  // namespace datasets and is always empty for a graph.
  tables?: DatasetTableFrameInfo[];
  // The dataset's schema projection, embedded by the backend from the same
  // frame enumeration as the arrays above, so counts and schema in one
  // response are one atomic snapshot.  This is the only schema source — the
  // schema views derive from it instead of fetching GET /schema.
  readonly graph_schema: RawGraphSchema;
}

/**
 * A frame's leading rows plus its column types -- the "quick view" of a frame.
 * `num_rows` is the frame's full height, not the number returned, so a caller
 * can say how much of it is on screen.
 */
export interface FramePreviewResponse {
  frame_name: string;
  num_rows: number;
  offset: number;
  schema: SchemaProperty[];
  // Which column identifies a row, for naming rows rather than describing them
  // (picking graph seeds, say).  Null for anything but a vertex frame.
  key?: string | null;
  data: XgtValue[][];
  // Present when row_id columns reference graph entities. This is the same
  // indexed representation returned for query answers: each structured cell
  // points into one of these frame rows, and column_frames says which frames
  // its first tuple index addresses.
  frames?: Record<string, FrameData>;
  column_frames?: (string[] | null)[];
}

// -----------------------------------------------------------------------
// Job types
// -----------------------------------------------------------------------

export type JobStatus = 'scheduled' | 'running' | 'suspended' | 'blocked' |
                        'completed' | 'canceled' | 'failed' | 'rollback' |
                        'unknown_job_status';

// Coarse execution phase of a running job.  Loads progress reading ->
// building -> committing; queries use executing -> committing; egests (saves)
// use writing.
export type JobPhase = 'reading' | 'building' | 'executing' | 'committing' |
                       'writing' | 'done';

// Live execution progress for a running job (absent for queued/finished jobs).
// `percent` is scoped to the CURRENT phase, not the whole job, and is null when
// no trustworthy denominator exists — fall back to `substep` / rows.
export interface JobProgress {
  phase: JobPhase;
  substep: string | null;
  rows_processed: number;
  rows_total: number | null;
  percent: number | null;
  // True when `percent` is a byte-based estimate, not an exact row fraction.
  percent_is_estimate: boolean;
  phase_elapsed_ms: number;
  // Edges traversed so far by a running query (live, exact); 0 for non-query
  // jobs.  The client formats it for display.
  traversed_edges: number;
  // Id of the job this progress belongs to, so a caller can correlate it with
  // the Jobs list or cancel it.  Absent when the xGT server does not report
  // it.
  job_id?: number | null;
}

/// A page of the jobs list.
export interface JobsPage {
  offset: number;
  limit: number;
  items: Job[];
  // Priority view only: more active jobs exist than the server orders as one
  // set, so the ordering covers the first batch of them rather than the whole
  // active set.  Absent on the other views.
  active_truncated?: boolean;
}

export interface GetJobsOptions {
  job_status?: string;
  offset?: number;
  limit?: number;
  start_date?: string;
  end_date?: string;
  description?: string;
  user?: string;
  dataset?: string;
  min_runtime_s?: number;
  max_runtime_s?: number;
  min_num_rows?: number;
  max_num_rows?: number;
  min_job_id?: number;
  max_job_id?: number;
  newest_first?: boolean;
  // When false, the server omits other users' active jobs (only your own are
  // returned).  Defaults to true server-side; sent only to opt out.
  others_active?: boolean;
  // "Priority" view: the server orders the active jobs as one set (execution
  // order, then descending priority) above the recency-ordered rest, across
  // page boundaries rather than within a page.  Defaults to false; when false
  // the page is strict submit order by id.
  prioritize_active?: boolean;
  // Tags the request as an automated background poll (live-mode refresh) so the
  // backend does not treat it as user activity for the inactivity timeout.  Not
  // a query parameter — stripped before building the URL.
  background?: boolean;
}

export interface Job {
  id: number | string;
  status: JobStatus;
  description: string;
  user: string;
  dataset: string | null;
  submit_time: string | null;
  start_time: string | null;
  end_time: string | null;
  num_rows: number | null;
  // Scheduling priority the user sets (1..40, higher runs sooner; 20 is
  // normal).  Absent when the xGT server does not report it.
  priority?: number | null;
  // The scheduler's live score: higher runs sooner, combining the priority,
  // time waited (queued or suspended), and the owner's fair-share standing.
  // Meaningful only while a job is queued, running, or suspended; absent when
  // the xGT server does not report it.
  scheduling_score?: number | null;
  // Highest priority a non-admin owner may set for the job (its cap): 20 by
  // default, or whatever an admin last set.  Bounds the priority control.
  priority_cap?: number | null;
  // Sampled CPU core-seconds the job consumed (statistical).  Absent when the
  // xGT server does not report it.
  cpu_seconds?: number | null;
  // Average CPU cores the job used while running (cpu_seconds / run time):
  // its average parallelism.  Absent when the xGT server does not report it.
  cpu_utilization?: number | null;
  // CPU cores the job is using right now (live EWMA); meaningful only while
  // running.  Absent when the xGT server does not report it.
  cpu_cores_current?: number | null;
  // Times the scheduler suspended this job so another could run.  Counted on
  // the server because a suspend and resume that both complete between two
  // polls leaves no trace a client could see.  Absent when the xGT server
  // does not report it.
  suspend_count?: number | null;
  error?: string;
  results_frame: string | null;
  query_plan?: string | null;
  // Live execution progress; present only while the job is running.
  progress?: JobProgress | null;
  // Edges traversed per Cypher label.  Live (approximate) while a query runs,
  // authoritative once finished; absent for non-query jobs.
  visited_edges?: Record<string, number> | null;
}

// -----------------------------------------------------------------------
// Frame data types (query answer data)
// -----------------------------------------------------------------------

export interface FramePropertySchema {
  name: string;
  type: string;
}

interface BaseFrameData {
  schema: FramePropertySchema[];
  data: XgtValue[][];
}

interface NodeFrameData extends BaseFrameData {
  key: string;
}

interface EdgeFrameData extends BaseFrameData {
  sourceFrame: string;
  targetFrame: string;
  sourceKey: string;
  targetKey: string;
}

export type FrameData = NodeFrameData | EdgeFrameData;

export interface BookmarkData {
  _id: string;
  /**
   * When it was last opened, ISO 8601.  Absent on a bookmark nothing has opened since the
   * server began recording it, and on every bookmark until it does.
   */
  last_used?: string;
  origin_id: string;
  name?: string;
  query?: string;
  question?: string;
  guidance?: string;
  path: string;
  dataset_name?: string;
  bookmark_id?: string | null;
}

export interface HistoryEntry {
  _id: string;
  timestamp: string;
  bookmark_id?: string | null;
  dataset_name?: string;
  query?: string;
  question?: string;
  guidance?: string;
}

// --- New types ---

export interface UploadSettings {
  blockSize?: string | number;
  blockUnit?: string;
  delimiter?: string;
  headerMode?: string;
  duplicateKeys?: string;
  suppressErrors?: boolean;
}

export interface ConnectorInfo {
  connectionName: string;
  dbType: string;
  connectionString: string;
  icon?: string;
  modified?: boolean;
}

/** A page of an edge frame drawn as a graph: the edges as read, plus the
 *  endpoint vertices they name.  `frames` is keyed by display name and shaped
 *  for PrepareDataForAnswerGraph, the same as a query answer's frames. */
export interface FrameGraphResponse {
  readonly frame_name: string;
  // The frame's full height, so a caller can say which window it is showing.
  readonly num_rows: number;
  readonly offset: number;
  readonly frames: Record<string, FrameData>;
  // Vertex frames only: how far the expansion actually reached and how many rows
  // it started from, both after the server's caps.
  readonly depth?: number;
  readonly seed_count?: number;
  // Hops actually taken, and why there were no more.  A walk that fills up on
  // edges or runs out of time returns what it found, which cannot be told from a
  // finished one without this -- and the difference is the whole answer to "is
  // that all there is?". The budget is here for the same reason: it is the
  // effective value after the server applies its default or cap.
  readonly hops_done?: number;
  readonly stopped?: 'complete' | 'budget' | 'deadline' | 'truncated';
  readonly edge_budget?: number;
  // The per-node cap the walk ran with, and which of the samples it drew.
  // Both null where nothing was capped: every edge is then drawn and there is
  // no other picture a different seed could have given.
  readonly fanout?: number | null;
  // Which walk drew this. False where the server has no sample_neighbors and
  // the read was stepped instead, which is the case the per-node caps and the
  // sample cannot be honoured in.
  readonly sampled?: boolean;
  // Which of the two totals stopped the walk, where one did.
  readonly stopped_by?: 'nodes' | 'edges' | null;
  readonly sample_seed?: number | null;
  // Which frames the walk started in.  frame_name alone cannot say once a walk
  // may be seeded from more than one.
  readonly seed_frames?: readonly string[];
  // And the rows it started at, frame and key together, in this answer's own
  // spelling of the frame names -- which is what `frames` below is keyed by.
  // Not derivable from the request: the names are resolved server-side, the keys
  // are coerced to the key column's type, and the cap is applied there.
  readonly seeded?: readonly (readonly [string, string | number])[];
}

/** A whole dataset drawn as a graph: a share of every edge frame in it, plus
 *  the vertex frames nothing connects to.  A sample, and the counts say which
 *  one -- `sampled_rows` of `num_rows`. */
export interface DatasetGraphResponse {
  readonly frame_name: string;
  readonly num_rows: number;
  readonly offset: number;
  readonly sampled_rows: number;
  // Frames with nothing in them: named rather than drawn, since there is
  // nothing to draw and being empty is the interesting part.
  readonly empty_frames: readonly string[];
  readonly frames: Record<string, FrameData>;
}

/** What a dataset-wide clear did.  `append_only_frames` are the frames the
 *  server would not empty: a container a query wrote with INTO only accepts new
 *  rows.  Not a failure -- everything else was cleared -- but the caller has to
 *  say so rather than claim the whole dataset is empty. */
export interface ClearDatasetResponse {
  datasets: DatasetInfo[];
  append_only_frames: string[];
}

export interface UserProfile {
  username?: string;
  fullName?: string;
  email?: string;
  theme?: string;
  connectors?: Record<string, ConnectorInfo>;
  uploadSettings?: UploadSettings;
  userIsAdmin?: boolean;
  showBookmarkEditor?: boolean;
  // Whether a graph reports the rate it is being drawn at.  A preference about how this
  // person likes to work, so it follows them to another machine rather than staying in the
  // browser they set it in.
  showFrameRate?: boolean;
  // How the navigation renders its entries: 'icons', 'titles', or
  // 'icon-titles'. A preference about how this person likes to work, so it
  // follows them rather than staying in the browser they set it in. An
  // unrecognized value draws both, which is the safe reading of a value
  // nobody here wrote.
  displayMode?: string;
  experimental_features_enabled?: string[];
  dataset_ai_guidance?: Record<string, string>;
  activeDataset?: string;
  activeLLM?: string;
  // Server-minted, server-owned: the key names are digested with before any of
  // them go into localStorage.  Held in memory for the session only -- writing
  // it to disk beside the digests would defeat the point.
  readonly state_key?: string;
  // Graph searches are small, user-owned preferences rather than Cypher query
  // history.  They live on the server-backed profile so recoverable search
  // text is never written to the browser's plaintext storage.
  graphSearchHistory?: GraphSearchRecord[];
  graphSearchBookmarks?: GraphSearchRecord[];
  // Dynamic LLM credential fields accessed by cred_id.
  [key: string]: unknown;
}

export interface GraphSearchRecord {
  query: string;
  timestamp: string;
}

/** The identity value stored on the ApiClient session state (set by
 *  AuthService).  Can be a string (username) or an object with a token for
 *  Bearer auth. */
export type AuthUser = string | { token: string };

/** Response from the login endpoint. Also used by completeLogin(). */
export interface LoginResponse {
  login: boolean;
  identity?: string;
  error?: string;
}

/** Application configuration returned by getAppConfig(). */
export interface AppConfig {
  auth_types?: string[];
  default_xgt_host?: string;
  default_xgt_port?: string;
  llm_config?: Record<string, { display_name?: string }>;
  llm_credentials?: Record<string, LlmProviderConfig>;
  experimental_features?: Record<string, ExperimentalFeature>;
  logout_redirect_url?: string;
  version?: string;
  // Server-side inactivity timeout in seconds (MC_SESSION_TTL).  Drives the
  // frontend idle-timeout controller's expiry clock so the warning modal and
  // keepalive stay in lockstep with the backend.
  session_ttl?: number;
}

// -----------------------------------------------------------------------
// Plugin contract (mirrors backend `surfaces.info` design doc).
// -----------------------------------------------------------------------

/** Recognized surface kinds.  The host has one renderer per kind; the
 *  manifest key (e.g. `info`, `test_ui`, `info_compliance`) is a free-form
 *  identifier, not a contract.  Adding a new kind is a three-step change:
 *  add it here, add a renderer component, add an arm to the dispatch in
 *  `Header.tsx`. */
export type PluginSurfaceKind = 'json' | 'webview';

interface PluginSurfaceCommon {
  // Absolute path returned by the backend (mount + manifest URL already
  // joined). Pass it through unchanged — do not prepend `/api/...`.
  url: string;
  // Pre-resolved label: the plugin's manifest `label`, or `name` as fallback.
  label: string;
  // The container this surface opens in. Absent means `modal`, which is what
  // every surface written before this field existed gets. A form is a
  // preference rather than a command: a container substitutes where it cannot
  // honor one, the way the mobile drawer opens every surface as a modal.
  form?: PluginSurfaceForm;
  // Opening hints for `form: 'panel'`. Required when that form is declared
  // and rejected otherwise, so there is exactly one way to ask for a panel.
  panel?: PluginPanelConfig;
}

/** A JSON-shaped surface.  The host fetches `url`, expects a
 *  `PluginInfoPayload`, and renders it inside `PluginInfoModal`. */
export interface PluginJsonSurface extends PluginSurfaceCommon {
  kind: 'json';
}

/** A web-view surface.  The host embeds `url` in a same-origin iframe inside
 *  `PluginIframeModal`; the plugin owns rendering inside the frame.  Auth
 *  flows because the iframe is same-origin: the browser sends the JWT cookie
 *  on the document fetch and on any AJAX the embedded page issues back to
 *  the plugin's mount. */
export interface PluginWebviewSurface extends PluginSurfaceCommon {
  kind: 'webview';
}

/** Discriminated union over all surface kinds the host knows how to render.
 *  An unrecognized `kind` is rejected at validation time (see
 *  `collectPluginSurfaceRows`) and again at the dispatch site via
 *  `assertNever`.  At v0.0.x of the plugin contract a kind the frontend does
 *  not handle is a contract bug between this repo and the backend, so it
 *  surfaces as a loud throw rather than a silent skip. */
export type PluginSurface = PluginJsonSurface | PluginWebviewSurface;

/** Surface dictionary on a plugin manifest.  Keys are free-form identifiers
 *  chosen by the plugin author so a single plugin can ship multiple surfaces
 *  of the same kind (e.g. `info_main` + `info_compliance` both `kind: 'json'`,
 *  or `dashboard` + `admin_ui` both `kind: 'webview'`).  Dispatch is on
 *  `value.kind`, not on the key. */
type PluginSurfaces = Record<string, PluginSurface>;

/** A plugin loaded by the composite shell, as serialized into the
 *  `/api/plugins/surfaces` response. Only effectively-available plugins appear
 *  here (the backend omits inactive or dependency-blocked ones), so there is
 *  no per-plugin enable flag on this shape — every entry is available and its
 *  surfaces are already filtered to the enabled ones. The admin control view
 *  (every loaded plugin, with state) is `AdminPlugin`, fetched separately from
 *  `/api/admin/plugins`. */
export interface ActivePlugin {
  name: string;
  description: string;
  mount: string;
  plugin_api_version: number;
  surfaces: PluginSurfaces;
}

/** Response shape of `GET /api/plugins/surfaces`. */
export interface PluginSurfacesResponse {
  plugins: ActivePlugin[];
}

/** Every presentation form the surface contract names, whether this host can
 *  render it yet or not. Reserving a name costs one entry here and spares a
 *  breaking change later: a manifest naming a form in this list but not in
 *  `IMPLEMENTED_SURFACE_FORMS` is told the host has not built it, while a name
 *  outside the list is reported as the typo it almost certainly is. */
export const PLUGIN_SURFACE_FORMS = ['modal', 'panel', 'sidebar'] as const;

export type PluginSurfaceForm = typeof PLUGIN_SURFACE_FORMS[number];

/** The forms this host actually renders. `sidebar` -- a docked, resizable
 *  edge rail -- is named above and not built; it is the form commercial
 *  extension APIs converged on, so it is reserved before anyone picks a
 *  different word for it. */
export const IMPLEMENTED_SURFACE_FORMS = ['modal', 'panel'] as const;

export type ImplementedSurfaceForm =
  typeof IMPLEMENTED_SURFACE_FORMS[number];

/** Where a panel first appears. Six named positions rather than free
 *  coordinates: a manifest cannot know the user's viewport, and an anchor is
 *  also what a keyboard user picks from the panel menu in place of dragging. */
export const PANEL_ANCHORS = ['top-left', 'top-right', 'center-left',
  'center-right', 'bottom-left', 'bottom-right'] as const;

export type PanelAnchor = typeof PANEL_ANCHORS[number];

/** Opening hints for a surface whose declared form is `panel`.
 *
 *  The block is configuration, not a declaration: `form` says the surface
 *  wants a panel and this says what the panel should look like when it
 *  opens.
 *
 *  Wire names are snake_case, matching the rest of the plugin manifest
 *  contract; they are mapped to the camelCase `PanelSpec` at registration.
 *  Sizes are opening hints in CSS pixels, clamped to the room actually
 *  available on every open, restore, and window resize. */
export interface PluginPanelConfig {
  // Pill text and panel title. Capped at `PANEL_TITLE_MAX_LENGTH`: the header
  // cannot absorb a longer one, and the overflow ladder drops labels before
  // it drops pills.
  readonly title: string;
  // Name from the host's allowlist (`panelIconNames()`). An unrecognized name
  // draws the fallback glyph rather than throwing -- a wrong icon is cosmetic,
  // and the allowlist is owned here, not by the manifest's author.
  readonly icon?: string;
  readonly width: number;
  readonly height: number;
  readonly anchor: PanelAnchor;
  readonly min_width?: number;
  readonly min_height?: number;
  // Absent means true.
  readonly resizable?: boolean;
  // Absent means true. False suppresses the close control, leaving iconify as
  // the only dismissal, for a surface that must stay mounted once started.
  readonly closable?: boolean;
  // Absent means false. True mounts the panel shortly after login rather than
  // on first open, for a surface that must accumulate state while iconified.
  readonly preload?: boolean;
}

/** Longest pill title the header can lay out. Over-length throws at
 *  validation rather than truncating: truncation hides an author's mistake
 *  behind a layout that still looks plausible. */
export const PANEL_TITLE_MAX_LENGTH = 24;

/** Per-surface admin state in the `AdminPlugin` view. The admin control panel
 *  renders one toggle per surface, so this carries the display `label` and the
 *  `kind` alongside the gate itself -- the user-facing `PluginSurface` shape
 *  (which does carry a label) is not returned by the admin endpoint.
 *
 *  Nothing here describes the surface's form. Which container it opens in is
 *  the manifest's business and an administrator cannot change it, so
 *  reporting it would be a fact the backend has to keep true for no reader. */
export interface AdminPluginSurfaceState {
  // Human-readable label: the plugin manifest `label`, or the surface key as
  // fallback. Rendered as the admin sub-row's primary text.
  label: string;
  // Surface kind, mirroring the user-facing `PluginSurface` discriminant.
  kind: PluginSurfaceKind;
  // The administrator's gate. Seeded from the deployment's site-config and
  // overridden here; a disabled surface is omitted from the user-facing
  // payload entirely rather than hidden by the client.
  enabled: boolean;
}

/** A loaded plugin as seen by the admin control panel (`GET /api/admin/plugins`).
 *  Unlike `ActivePlugin`, this lists *every* loaded plugin — active or not —
 *  with both the explicit toggle and the effective, dependency-resolved state. */
export interface AdminPlugin {
  name: string;
  mount: string;
  description: string;
  requires_plugins: string[];
  // Explicit admin on/off toggle. The Admin switch binds to this.
  readonly active: boolean;
  // Effective state after the dependency cascade (active AND every required
  // plugin available). Read-only; derived by the backend.
  readonly available: boolean;
  // When `active` is true but `available` is false, the name of the dependency
  // holding this plugin down; otherwise null.
  readonly blocked_by: string | null;
  surfaces: Record<string, AdminPluginSurfaceState>;
}

/** Response shape of `GET /api/admin/plugins`. */
export interface AdminPluginsResponse {
  plugins: AdminPlugin[];
}

/** Response shape of `PUT /api/admin/plugins/<name>`. Returns the full
 *  post-write admin view (so the client seeds its cache from the response)
 *  plus optional hints describing the toggle's dependency cascade. */
export interface AdminPluginUpdateResponse {
  plugins: AdminPlugin[];
  // Present when disabling a plugin newly blocked its dependents.
  dependents_blocked?: string[];
  // Present when enabling a plugin that is still blocked by an unmet requirement.
  unmet_requires?: string[];
}

// -----------------------------------------------------------------------
// Admin AI-model gating (mirrors backend `admin-ai-model-gating` design doc).
// -----------------------------------------------------------------------

/** A configured AI model as seen by the admin control panel
 *  (`GET /api/admin/aimodels`). `key` is the same key the model dropdown uses
 *  (`AppConfig.llm_config`), and the list arrives in server configuration
 *  order, grouped by provider. The user-facing `llm_config` is the backend's
 *  filtered view of this list, so a toggle here needs an `AppConfig` refresh
 *  to reach the dropdown. */
export interface AdminAiModel {
  key: string;
  // Human-readable name; the accessible name for the row's controls.
  display_name: string;
  provider: string;
  // The provider's model id, e.g. `claude-fable-5-1`.
  model: string;
  // Effective state: the admin override when one exists, else the YAML
  // default. The Admin switch binds to this.
  readonly enabled: boolean;
  // The YAML default the model returns to when its override is cleared.
  readonly default_enabled: boolean;
  // Whether an admin override exists in MongoDB. `modified_by` and
  // `modified_at` are null when it does not.
  readonly overridden: boolean;
  readonly modified_by: string | null;
  readonly modified_at: string | null;
  // Name of the ADEL profile bound to this model. Every model has exactly
  // one, the shipped `default` until reassigned; the ADEL Profile column's
  // Select binds to it.
  readonly permission_profile: string;
}

/** Response shape of `GET`, `PUT` and `DELETE` on `/api/admin/aimodels`.
 *  Every write returns the full recomputed view so the client seeds its cache
 *  from the response. The admin pane caches this whole object, not just the
 *  list, which is why every response carries both fields. */
export interface AdminAiModelsResponse {
  models: AdminAiModel[];
  /** Override entries in the backend's store whose key matches no configured
   *  model, because the model was dropped from the YAML after an admin had
   *  overridden it. They have no effect while the key is absent, but they
   *  revive with their stale attribution if it is ever configured again, and
   *  nothing in `models` can show them. A per-model write never changes this
   *  count; only a global reset clears them. */
  readonly orphaned_overrides: number;
}

// -----------------------------------------------------------------------
// ADEL profiles (see docs/design/agent-runtime.md).
// -----------------------------------------------------------------------

/** Exposure levels, most to least restrictive. Cumulative: a level permits
 *  everything every level before it permits. */
export const EXPOSURE_LEVELS = [
  'hidden', 'schema_only', 'unique_values', 'statistical', 'sample', 'full'
] as const;

export type ExposureLevel = typeof EXPOSURE_LEVELS[number];

/** One level override, scoped to a graph and optionally narrowed to a frame
 *  or a property within it. `frame` and `property` are null at the graph
 *  scope; `property` is null at the frame scope. */
export interface LevelEntry {
  readonly graph: string;
  readonly frame: string | null;
  readonly property: string | null;
  readonly level: ExposureLevel;
}

/** An ADEL profile as the admin editor and the backend exchange it
 *  (`/api/admin/permission_profiles`). `version` guards a save against a
 *  concurrent edit elsewhere; `assigned_models` lists the AI-model keys
 *  currently bound to this profile. */
export interface PermissionProfile {
  readonly name: string;
  readonly version: number;
  readonly default_level: ExposureLevel;
  readonly cardinality_threshold: number;
  readonly entries: readonly LevelEntry[];
  readonly modified_by: string | null;
  readonly modified_at: string | null;
  readonly assigned_models: readonly string[];
}

/** Response shape of `GET` on `/api/admin/permission_profiles`, and of a
 *  delete, which answers with the remaining list. */
export interface PermissionProfilesResponse {
  profiles: PermissionProfile[];
}

/** The agent runtime's debugging switches, as `/api/admin/agent_debug`
 *  reads and sets them. `record_summary_jobs` keeps the summary tools'
 *  queries in the xGT job history, so they show on the Jobs page.
 *  `allow_create_and_load` lets the agent create a graph and load data;
 *  while it is off, users do both on the Graphs page. */
export interface AgentDebugSettings {
  readonly record_summary_jobs: boolean;
  readonly allow_create_and_load: boolean;
}

/** The editable fields of a profile, as the admin editor sends them on
 *  create or save. `name` is immutable after create, so a save both carries
 *  it and names the profile in the request path. */
export interface ProfileDraft {
  name: string;
  default_level: ExposureLevel;
  cardinality_threshold: number;
  entries: LevelEntry[];
}

/** A label/value row inside a plugin info section. Non-string values are
 *  coerced to text at render time (`1 → "1"`, `true → "true"`, `null → ""`). */
export interface PluginInfoItem {
  label: string;
  value: string | number | boolean | null;
}

/** A titled group of label/value rows in the plugin info modal. */
interface PluginInfoSection {
  title?: string;
  items: PluginInfoItem[];
}

/** Payload returned by a plugin's info surface URL. The frontend treats any
 *  `schema_version` other than 1 as a fetch error and falls back to the
 *  manifest data. */
export interface PluginInfoPayload {
  schema_version: 1;
  title?: string;
  sections: PluginInfoSection[];
}

/** Per-provider LLM credential configuration from the backend. */
export interface LlmProviderConfig {
  display_name?: string;
  fields: LlmField[];
}

/** A single credential field within an LLM provider config. */
export interface LlmField {
  cred_id: string;
  display_name: string;
  type: string;
  options?: string[];
  pattern?: string;
  mask?: boolean;
}

/** Experimental feature definition from the backend. */
export interface ExperimentalFeature {
  display_name: string;
  description: string;
}

/** xGT server configuration returned by getConfigXgt(). */
export interface ConfigXgt {
  config: Record<string, string | number | boolean>;
  version?: string;
  client_version?: string;
  expiration_date?: string;
}

/** Payload for setConfigXgt() — a subset of config keys to update. */
export type ConfigXgtUpdate = Record<string, string | number>;

/** Status data passed to the asyncStatus callback during polling.
 *  Callers that need additional fields (e.g. execution_trace) supply
 *  a more specific type via the asyncStatus<T>() generic. */
export interface AsyncStatusResponse {
  answer?: string;
  error?: string;
  error_type?: string;
  is_error?: boolean;
  // An agent turn's reply text streamed so far, while in progress.
  partial_text?: string;
}

// -----------------------------------------------------------------------
// Execution trace types (returned by async status during LLM operations)
// -----------------------------------------------------------------------

type JsonRepresentation = XgtValue | Record<string, XgtValue> | XgtValue[];

interface ExecutionAction {
  tool: string;
  parameters?: JsonRepresentation;
}

interface ExecutionObservation {
  success: boolean;
  error?: string;
  result?: JsonRepresentation;
}

export type ExecutionStepState = 'thinking' | 'acting' | 'observing';

export interface ExecutionStep {
  step: number;
  state: ExecutionStepState;
  label?: string;
  thought?: string;
  action?: ExecutionAction;
  observation?: ExecutionObservation;
}

export interface ExecutionTrace {
  execution_trace?: ExecutionStep[];
  step_count?: number;
  message?: string;
}

/** Parameters for deleteHistory(). Two shapes depending on operation. */
export type DeleteHistoryParams =
  | {
    category: string;
    filterQuery: string | null;
    startDate: string | null;
    endDate: string | null;
  }
  | {
    category: string;
    singleEntry: { id: string };
  };

/** Memory usage data returned by getMemory(). */
export interface MemoryData {
  footprint?: string;
  max_memory?: string;
}

/** Folder object returned by getBookmarks with bookmarkType 'folders'. */
export interface BookmarkFolder {
  _id?: string;
  folder_name: string;
  path?: string;
}

// -----------------------------------------------------------------------
// Query / answer response types
// -----------------------------------------------------------------------

/** Column metadata in a query answer schema. */
interface StructuredColumnMeta {
  name: string;
  frames: string[];
}

type SchemaColumn = StructuredColumnMeta | string[];

/** Scalar provenance from the same mapping that constructs the answer graph. */
export interface QueryColumnRole {
  frame: string;
  kind: 'vertex' | 'edge';
  property: string;
  roles: ('key' | 'source' | 'target')[];
  vertex_frames: string[];
}

/** Job metadata returned by submitQuery (subset of full Job). */
interface QueryJob {
  id: string;
  submit_time: string;
  start_time: string;
  end_time: string;
  status: string;
  description: string;
  dataset: string;
  results_frame: string;
  error?: string;
}

/** Response from the submitQuery endpoint. */
export interface SubmitQueryResponse {
  job: QueryJob;
  error?: string;
}

/** Response from getAnswer — full result data for a completed query. */
export interface QueryAnswerResponse {
  job: QueryJob;
  is_completed: boolean;
  num_rows: number;
  schema?: SchemaColumn[];
  data: XgtValue[][];
  frames?: Record<string, FrameData>;
  column_roles?: (QueryColumnRole | null)[];
  display_name_map?: Record<string, string>;
  error?: string;
  warning?: string;
  // Set by the backend when the frames feeding the inferred graph have
  // changed since the query ran (the row-id ABA hazard).  The table data
  // is still valid; only the graph visualization must be hidden.
  results_stale?: boolean;
}

// -----------------------------------------------------------------------
// Schema / upload response types
// -----------------------------------------------------------------------

/** Response from upload/upload_status when a load completes successfully. */
export interface UploadStatusResult {
  rows: string;
  bytes?: string;
}

/**
 * In-progress upload-status response.  Returned (instead of the bare
 * 'In Progress' string) while the server-side load runs and live job
 * progress is available, so the upload UI can show a real bar.
 */
export interface UploadInProgress {
  status: 'In Progress';
  // Live load progress; its job_id names the load's job as soon as the load
  // starts, so the UI can follow or link to it.
  progress?: JobProgress;
}

/** Response from createDataset. */
export interface CreateGraphSchemaResponse {
  success: boolean;
  renames?: Record<string, string> | null;
}

/**
 * One input's schema as inferred by the backend.  `id` is the source id sent
 * with the request and echoed back, and is the only reliable way to tell which
 * input an entry describes: `name` is a hint for the LLM, and carries the
 * placeholder 'frame' for every link and query.
 */
export interface InferredFileSchema {
  // Identified by id alone: the request's `name` is the file name or the
  // link's url, and a copy of it here had no reader.
  id: string;
  properties: [string, ...unknown[]][];
  filename?: string;
  // What xGT found the input to be from its bytes.  For a link it is the one
  // answer a client, which never sees the bytes, can correct its URL guess
  // by; absent for a query, which is not a file of any type, and for a link
  // probed by an xGT client that does not yet report a type (before 2.8.0).
  contentType?: 'csv' | 'parquet';
  // The reading these columns are columns of.  Present together: an input with
  // no delimiter (parquet, a query) has no header mode either, and columns
  // read under a different pair are different columns.
  inferredDelimiter?: string;
  headerMode?: string;
}

/**
 * Response from inferFileSchema (after asyncStatus polling), and the shape of
 * generation's `fileSchema`: one entry per input, in request order.  Both come
 * from one inferer, so a new input type is described the same way to both.
 */
export type InferFileSchemaResponse = InferredFileSchema[];

// -----------------------------------------------------------------------
// Bookmark mutation response types
// -----------------------------------------------------------------------

/** Response from addBookmark. */
export interface AddBookmarkResponse {
  bookmark: string;
}

/** Response from addBookmarkFolder. */
export interface AddBookmarkFolderResponse {
  folder: string;
}

/** Response from getBookmark — wraps a single BookmarkData. */
export interface GetBookmarkResponse {
  item: BookmarkData;
}
